"use client";

// ── Public judge library hooks (EX-PUB01) ───────────────────────────────────
// Keys are local and carry the actor-sensitive flag where one exists, the
// `features/judge-authoring/hooks.ts` precedent: the same list URL answers
// differently signed in and out (solved state), so the cache must not serve one
// viewer's answer to the other.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchJudgeProblem,
  fetchJudgeProblemEditorial,
  fetchJudgeProblemStats,
  fetchJudgeProblemSubmissions,
  fetchJudgeProblems,
  fetchJudgeSubmissionSource,
  fetchJudgeTopics,
  fetchJudgeRuntimes,
  fetchMySubmissions,
  fetchPracticeHints,
  fetchPracticeSubmission,
  revealPracticeHint,
  runPracticeSample,
  submitPractice,
} from "./api";
import { isPracticeSubmissionPending } from "./types";
import { useAuth } from "@/components/providers/auth-provider";
import { pendingPollInterval } from "@/lib/pending-poll";
import type {
  JudgeProblemListParams,
  JudgeProblemSubmissionsParams,
  PracticeSubmitInput,
  RevealPracticeHintInput,
  RunSampleInput,
  SubmissionHistoryParams,
} from "./types";

export const judgeKeys = {
  problems: (viewer: string, params: JudgeProblemListParams) =>
    ["judge", "problems", viewer, params] as const,
  problem: (viewer: string, slug: string) => ["judge", "problem", viewer, slug] as const,
  editorial: (slug: string) => ["judge", "editorial", slug] as const,
  stats: (viewer: string, slug: string) => ["judge", "stats", viewer, slug] as const,
  problemSubmissions: (viewer: string, slug: string, params: JudgeProblemSubmissionsParams) =>
    ["judge", "problem-submissions", viewer, slug, params] as const,
  source: (slug: string, submissionId: string) => ["judge", "source", slug, submissionId] as const,
  tags: () => ["judge", "problem-tags"] as const,
  runtimes: () => ["judge", "runtimes"] as const,
  submission: (id: string) => ["judge", "submission", id] as const,
  hints: (slug: string, locale: string) => ["judge", "hints", slug, locale] as const,
  mySubmissions: (params: SubmissionHistoryParams) =>
    ["judge", "my-submissions", params] as const,
};

/**
 * `viewer` distinguishes the anonymous answer from the signed-in one. The
 * user id itself is not needed — `fetcher` sends whoever's token is current,
 * and a login/logout flips `isAuthenticated`, which changes the key.
 */
export function useJudgeProblems(
  params: JudgeProblemListParams,
  viewerKey: string,
  enabled = true,
) {
  return useQuery({
    queryKey: judgeKeys.problems(viewerKey, params),
    queryFn: () => fetchJudgeProblems(params),
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useJudgeProblem(slug: string, viewerKey: string, enabled = true) {
  return useQuery({
    queryKey: judgeKeys.problem(viewerKey, slug),
    queryFn: () => fetchJudgeProblem(slug),
    enabled: enabled && !!slug,
    retry: false,
  });
}

/**
 * `EX-EDT01`: the same answer for every viewer, so the key carries no viewer.
 * `enabled` is the reader's click: nothing is fetched until they ask.
 */
export function useJudgeProblemEditorial(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: judgeKeys.editorial(slug),
    queryFn: () => fetchJudgeProblemEditorial(slug),
    enabled: enabled && !!slug,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * `EX-STA01`: the same answer for every viewer; fetched when the tab opens and
 * kept for a minute, the server's own view being a few minutes old anyway.
 */
export function useJudgeProblemStats(slug: string, enabled: boolean, viewerKey = "anon") {
  return useQuery({
    // `EX-SRC01`: a solver's answer carries submission ids; the key keeps viewers apart.
    queryKey: judgeKeys.stats(viewerKey, slug),
    queryFn: () => fetchJudgeProblemStats(slug),
    enabled: enabled && !!slug,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * `EX-SRC01`: the Submissions tab. The answer differs by viewer (`own`,
 * `canRead`), so the key carries the viewer, the library list's rule.
 */
export function useJudgeProblemSubmissions(
  slug: string,
  params: JudgeProblemSubmissionsParams,
  viewerKey: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: judgeKeys.problemSubmissions(viewerKey, slug, params),
    queryFn: () => fetchJudgeProblemSubmissions(slug, params),
    enabled: enabled && !!slug,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** `EX-SRC01`: one program, asked for when a row is opened. */
export function useJudgeSubmissionSource(slug: string, submissionId: string | null) {
  return useQuery({
    queryKey: judgeKeys.source(slug, submissionId ?? ""),
    queryFn: () => fetchJudgeSubmissionSource(slug, submissionId!),
    enabled: !!slug && !!submissionId,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** `EX-TAG01`: the library's topics with their counts (the server caches five minutes). */
export function useJudgeTopics() {
  return useQuery({
    queryKey: judgeKeys.tags(),
    queryFn: fetchJudgeTopics,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** The graded catalogue — the runner's table, never Playground's (`docs/24` §3.1). */
export function useJudgeRuntimes(enabled = true) {
  const { user } = useAuth();
  const slug = user?.accountKind === "contest_temp" ? user.tempContest?.slug : undefined;
  return useQuery({
    queryKey: [...judgeKeys.runtimes(), slug ?? "public"],
    queryFn: () => fetchJudgeRuntimes(slug),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSubmitPractice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PracticeSubmitInput) => submitPractice(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["judge", "my-submissions"] });
    },
  });
}

/** No cache to invalidate: a run persists nothing, so nothing downstream goes stale. */
export function useRunPracticeSample(slug: string) {
  return useMutation({
    mutationFn: (input: RunSampleInput) => runPracticeSample(slug, input),
  });
}

/** Polls while the submission is still queued/running, so the verdict lands without a refresh. */
export function usePracticeSubmission(id: string | null) {
  return useQuery({
    queryKey: judgeKeys.submission(id ?? "none"),
    queryFn: () => fetchPracticeSubmission(id as string),
    enabled: !!id,
    staleTime: 0,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return pendingPollInterval(query, isPracticeSubmissionPending(data) ? 1 : 0);
    },
  });
}

export function useMySubmissions(params: SubmissionHistoryParams, enabled = true) {
  return useQuery({
    queryKey: judgeKeys.mySubmissions(params),
    queryFn: () => fetchMySubmissions(params),
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * `EX-HNT01`: the reader's three steps. Signed in only; the answer is the
 * reader's own, and `fetcher` sends whoever's token is current.
 */
export function usePracticeHints(slug: string, locale: "en" | "bn", enabled: boolean) {
  return useQuery({
    queryKey: judgeKeys.hints(slug, locale),
    queryFn: () => fetchPracticeHints(slug, locale),
    enabled: enabled && !!slug,
    retry: false,
  });
}

/** `EX-HNT01`: one step; the answer is the whole list, written straight into the read's cache. */
export function useRevealPracticeHint(slug: string, locale: "en" | "bn") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevealPracticeHintInput) => revealPracticeHint(slug, input),
    onSuccess: (result) => {
      queryClient.setQueryData(judgeKeys.hints(slug, locale), result.hints);
      // The other language's list is the same steps in other words: read it again when it is next shown.
      void queryClient.invalidateQueries({
        queryKey: ["judge", "hints", slug],
        predicate: (query) => query.queryKey[3] !== locale,
      });
    },
    // An API answer is authoritative; a transport failure is retried once by the caller's own click.
    retry: false,
  });
}
